/**
 * Client-side sender for the Meta Conversions API relay.
 *
 * Fires alongside the client-side Pixel `fbq('track', ...)` call. The shared
 * `eventID` lets Meta dedup the two paths within a 7-day window, so this is
 * purely additive — it improves attribution quality on iOS 14.5+ and other
 * browsers where the client Pixel is throttled by ITP/ATT, without
 * double-counting conversions.
 *
 * Reads `_fbp` / `_fbc` cookies set by the Pixel base script and the stored
 * attribution context from `utm.ts`, then POSTs to `/api/meta-capi` (the
 * Vercel serverless relay defined in `api/meta-capi.ts`). The relay handles
 * sha256-hashing PII and adding IP/user-agent before forwarding to Meta.
 */

import { attributionToPayload } from './utm'

export type ConversionEventName =
  | 'Lead'
  | 'Schedule'
  | 'CompleteRegistration'
  | 'Contact'
  | 'ViewContent'

export interface CAPIUserData {
  /** Plain email — the server will hash it before forwarding to Meta. */
  email?: string
  /** Plain phone — the server will strip non-digits and hash. */
  phone?: string
}

export interface SendCAPIInput {
  eventName: ConversionEventName
  /** UUID matching the `eventID` passed to `fbq('track', ...)` for dedup. */
  eventID: string
  /** Standard Meta event params (value, currency, content_name, etc.). */
  customData?: Record<string, unknown>
  /** Optional PII for matching. Hashed server-side. */
  userData?: CAPIUserData
}

const ENDPOINT = '/api/meta-capi'

/**
 * Read a single cookie value by name. Returns undefined if missing.
 */
function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`),
  )
  return match ? decodeURIComponent(match[1]) : undefined
}

/**
 * Read the Meta-set `_fbp` browser cookie. Set by the Pixel base script on
 * every Pixel-loaded page. Format: `fb.1.<unix-ms>.<random>`.
 */
function readFbp(): string | undefined {
  return readCookie('_fbp')
}

/**
 * Read the Meta-set `_fbc` browser cookie. Set by the Pixel when an `fbclid`
 * is present in the URL. If the cookie is missing but we captured an
 * `fbclid` in our attribution layer, synthesize the value per Meta's spec
 * (`fb.1.<unix-ms>.<fbclid>`) so CAPI matching still works.
 */
function readOrSynthesizeFbc(): string | undefined {
  const direct = readCookie('_fbc')
  if (direct) return direct

  const attr = attributionToPayload()
  const fbclid = attr.fbclid
  if (!fbclid) return undefined

  // Per Meta spec: subdomain index = 1 (cross all subdomains), then unix-ms,
  // then the fbclid value.
  return `fb.1.${Date.now()}.${fbclid}`
}

/**
 * Fire-and-forget POST to `/api/meta-capi`.
 *
 * Returns a Promise that resolves whether the relay accepted the event;
 * callers should not block UI flow on the result. Errors are swallowed
 * (logged to console) so analytics never breaks user-facing flows.
 */
export async function sendToCAPI(input: SendCAPIInput): Promise<void> {
  try {
    const fbp = readFbp()
    const fbc = readOrSynthesizeFbc()

    // Merge the captured attribution context into customData so Meta sees
    // utm_campaign / utm_source / etc. on the server-side event too.
    const attribution = attributionToPayload()
    const customData = {
      ...attribution,
      ...(input.customData ?? {}),
    }

    const body = {
      eventName: input.eventName,
      eventID: input.eventID,
      eventTime: Math.floor(Date.now() / 1000),
      sourceUrl: typeof window !== 'undefined' ? window.location.href : undefined,
      customData,
      userData: {
        ...(input.userData ?? {}),
        ...(fbp ? { fbp } : {}),
        ...(fbc ? { fbc } : {}),
      },
    }

    // Use keepalive so the request survives a navigation that may happen
    // immediately after a CTA click (e.g. Schedule confirm → external redirect).
    await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    })
  } catch (err) {
    console.warn('[CAPI] send failed', err)
  }
}
